import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { 
  CreditCard, 
  Smartphone, 
  Wallet, 
  Lock, 
  ArrowLeft,
  Shield,
  CheckCircle,
  Tag,
  Truck,
  Clock,
  MapPin,
  User,
  Mail,
  Phone,
  Calendar,
  Eye,
  EyeOff,
  Gift,
  Percent,
  X,
  Plus,
  AlertCircle,
  Info
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import { useToast } from '../contexts/ToastContext';

import { customerService } from '../services/customerService';
import { eventsService } from '../services/eventsService';
import { paymentsService } from '../services/paymentsService';
import Footer from '../components/Footer';
import { Card, Button, LoadingSpinner } from '../components/ui';

interface CheckoutProps {
  orderItems?: Array<{
    eventId: string;
    eventTitle: string;
    ticketType: string;
    quantity: number;
    price: number;
    image?: string;
  }>;
}

interface PromoCode {
  id: string;
  code: string;
  type: 'percentage' | 'fixed';
  value: number;
  description: string;
  minAmount?: number;
  maxDiscount?: number;
  validUntil: Date;
  usageLimit?: number;
  usedCount: number;
  isActive: boolean;
}

const Checkout: React.FC<CheckoutProps> = ({ orderItems = [] }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const { openLogin } = useAuthModal();
  const { success, error: showError } = useToast();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      openLogin();
    }
  }, [isAuthenticated]);  // eslint-disable-line react-hooks/exhaustive-deps
  
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'applePay' | 'googlePay' | 'crypto' | 'cash'>('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCardDetails, setShowCardDetails] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState<PromoCode | null>(null);
  const [promoError, setPromoError] = useState('');
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const [availablePromoCodes] = useState<PromoCode[]>([]);
  
  const [formData, setFormData] = useState({
    email: user?.email || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
    billingAddress: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'JO',
    // Cash on delivery specific fields
    deliveryAddress: '',
    deliveryCity: '',
    deliveryNotes: ''
  });

  const [bookingType, setBookingType] = useState<'solo' | 'multi'>('solo');
  const [checkoutQuantity, setCheckoutQuantity] = useState(1);
  const [attendeeNames, setAttendeeNames] = useState<string[]>(['']);

  // Get order data from navigation state or use mock data
  const stateData = location.state;
  const [mockOrderItems, setMockOrderItems] = useState<Array<{
    eventId: string;
    eventTitle: string;
    ticketType: string;
    quantity: number;
    price: number;
    image?: string;
  }>>([]);

  // Handle WhatsApp redirect and load event data
  useEffect(() => {
    const loadEventData = async () => {
      const eventId = searchParams.get('eventId');
      const source = searchParams.get('source');
      
      if (eventId && source === 'whatsapp') {
        // Load event from WhatsApp redirect
        const event = await eventsService.getEventById(eventId);
        if (event) {
          setMockOrderItems([{
            eventId: event.id,
            eventTitle: event.title,
            ticketType: 'General Admission',
            quantity: 1,
            price: event.price,
            image: event.image
          }]);
          return;
        }
      }
    };
    
    loadEventData();

    // Use state data or default mock data
    if (stateData?.event) {
      const quantity = stateData.quantity || 1;
      setMockOrderItems([{
        eventId: stateData.event.id,
        eventTitle: stateData.event.title,
        ticketType: stateData.ticket.name,
        quantity: quantity,
        price: stateData.ticket.price,
        image: stateData.event.image
      }]);
      
      // Set booking type and attendee names if provided
      if (stateData.bookingType) {
        setBookingType(stateData.bookingType);
      }
      if (stateData.attendeeNames && Array.isArray(stateData.attendeeNames)) {
        setAttendeeNames(stateData.attendeeNames.length > 0 ? stateData.attendeeNames : Array(quantity).fill(''));
      } else {
        setAttendeeNames(Array(quantity).fill(''));
      }
      setCheckoutQuantity(quantity);
    } else if (orderItems.length > 0) {
      setMockOrderItems(orderItems);
      const qty = orderItems[0]?.quantity || 1;
      setCheckoutQuantity(qty);
      setAttendeeNames(Array(qty).fill(''));
    } else {
      // Default mock data
      setMockOrderItems([{
        eventId: '1',
        eventTitle: 'Calum Scott "Bridges" Asia Tour Jordan',
        ticketType: 'General Admission',
        quantity: 2,
        price: 100,
        image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop'
      }]);
      setCheckoutQuantity(2);
      setAttendeeNames(['', '']);
    }
  }, [searchParams, stateData, orderItems]);

  // Promo codes are now validated via API

  const subtotal = mockOrderItems.reduce((sum: number, item) => sum + (item.price * item.quantity), 0);
  
  // Calculate promo discount
  let promoDiscount = 0;
  if (appliedPromo) {
    if (appliedPromo.type === 'percentage') {
      promoDiscount = subtotal * (appliedPromo.value / 100);
      if (appliedPromo.maxDiscount) {
        promoDiscount = Math.min(promoDiscount, appliedPromo.maxDiscount);
      }
    } else {
      promoDiscount = appliedPromo.value;
    }
    promoDiscount = Math.min(promoDiscount, subtotal); // Can't discount more than subtotal
  }

  const discountedSubtotal = subtotal - promoDiscount;
  const processingFee = paymentMethod === 'cash' ? 0 : discountedSubtotal * 0.03; // No processing fee for cash
  const deliveryFee = paymentMethod === 'cash' ? 5 : 0; // Delivery fee for cash on delivery
  const taxes = discountedSubtotal * 0.16; // 16% VAT for Jordan
  const total = discountedSubtotal + processingFee + deliveryFee + taxes;

  const paymentMethods = [
    {
      id: 'card',
      label: 'Credit/Debit Card',
      icon: CreditCard,
      description: 'Visa, Mastercard, American Express',
      processingTime: 'Instant'
    },
    {
      id: 'applePay',
      label: 'Apple Pay',
      icon: Smartphone,
      description: 'Touch ID or Face ID',
      processingTime: 'Instant'
    },
    {
      id: 'googlePay',
      label: 'Google Pay',
      icon: Wallet,
      description: 'Quick & secure',
      processingTime: 'Instant'
    },
    {
      id: 'crypto',
      label: 'Crypto/NFT',
      icon: Wallet,
      description: 'ETH, SOL, MATIC + NFT receipt',
      processingTime: '5-10 minutes'
    },
    {
      id: 'cash',
      label: 'Cash on Delivery',
      icon: Truck,
      description: 'Pay when you receive tickets',
      processingTime: '24-48 hours'
    }
  ];

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Save customer information when ANY Contact Information field is filled
    const contactFields = ['firstName', 'lastName', 'email', 'phone', 'billingAddress', 'city', 'state', 'zipCode', 'country', 'deliveryAddress', 'deliveryCity', 'deliveryNotes'];
    if (contactFields.includes(field) && value.trim()) {
      // Debounce saving to avoid too many calls
      setTimeout(() => {
        saveCustomerInfo('info_filled');
      }, 1000);
    }
  };

  const saveCustomerInfo = (status: 'info_filled' | 'order_placed' | 'payment_completed') => {
    // For info_filled, save even with partial data (at least one field filled)
    // For order_placed and payment_completed, require all mandatory fields
    if (status === 'info_filled') {
      // Save if at least one of the key fields is filled
      if (!formData.firstName && !formData.lastName && !formData.email && !formData.phone) {
        return;
      }
    } else {
      // For order_placed and payment_completed, require all mandatory fields
      if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
        return;
      }
    }

    const eventData = mockOrderItems[0]; // Get first event data
    if (!eventData) return;

    // Include attendee names for multi-booking
    const attendeeNamesData = bookingType === 'multi' && attendeeNames.length > 0 
      ? attendeeNames.filter(name => name.trim())
      : [];

    const customerData: any = {
      eventId: eventData.eventId,
      eventTitle: eventData.eventTitle,
      firstName: formData.firstName || '',
      lastName: formData.lastName || '',
      email: formData.email || '',
      phone: formData.phone || '',
      billingAddress: formData.billingAddress || '',
      city: formData.city || '',
      state: formData.state || '',
      zipCode: formData.zipCode || '',
      country: formData.country || 'JO',
      deliveryAddress: formData.deliveryAddress || '',
      deliveryCity: formData.deliveryCity || '',
      deliveryNotes: formData.deliveryNotes || '',
      paymentMethod,
      status,
      isCompleted: status === 'payment_completed',
      bookingType: bookingType,
      attendeeNames: attendeeNamesData,
      orderData: status !== 'info_filled' ? {
        ticketType: eventData.ticketType,
        quantity: eventData.quantity,
        price: eventData.price,
        subtotal,
        total,
        promoCode: appliedPromo?.code,
        promoDiscount
      } : undefined
    };

    try {
      customerService.saveCustomerData(customerData);
    } catch (error) {
      console.error('Error saving customer data:', error);
    }
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) return;
    
    setIsApplyingPromo(true);
    setPromoError('');
    
    try {
      const validatedPromo = await paymentsService.validatePromoCode(promoCode, subtotal);
      
      if (!validatedPromo) {
        setPromoError('Invalid promo code');
        return;
      }
      
      // Convert API response to PromoCode format
      const promo: PromoCode = {
        id: validatedPromo.code,
        code: validatedPromo.code,
        type: validatedPromo.type,
        value: validatedPromo.value,
        description: validatedPromo.description,
        minAmount: validatedPromo.minAmount,
        maxDiscount: validatedPromo.maxDiscount,
        validUntil: new Date(), // API validates expiry, so we trust it
        usageLimit: 0, // Not needed for display
        usedCount: 0,
        isActive: true
      };
      
      setAppliedPromo(promo);
      setPromoCode('');
    } catch (error: any) {
      setPromoError(error.message || 'Failed to apply promo code');
    } finally {
      setIsApplyingPromo(false);
    }
  };

  const removePromoCode = () => {
    setAppliedPromo(null);
    setPromoError('');
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    // Validate required fields
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      alert('Please fill in all required fields');
      return;
    }

    if (mockOrderItems.length === 0) {
      alert('No items in cart');
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const eventData = mockOrderItems[0];
      
      // Create order via API
      const order = await paymentsService.createOrder({
        eventId: eventData.eventId,
        ticketType: eventData.ticketType,
        quantity: eventData.quantity,
        totalAmount: subtotal,
        discount: promoDiscount,
        finalAmount: total,
        paymentMethod,
        promoCode: appliedPromo?.code,
        billingInfo: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address: formData.billingAddress,
          city: formData.city,
          country: formData.country,
          zipCode: formData.zipCode
        },
        deliveryInfo: paymentMethod === 'cash' ? {
          method: 'delivery',
          address: formData.deliveryAddress,
          city: formData.deliveryCity,
          notes: formData.deliveryNotes
        } : undefined
      });
      
      // Save customer order data
      saveCustomerInfo('order_placed');
      
      // For non-cash payments, simulate processing
      if (paymentMethod !== 'cash') {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      
      // Update status to payment completed
      saveCustomerInfo('payment_completed');
      
      // Navigate to success page with order data
      success('Order placed successfully!');
      navigate('/payment-success', {
        state: {
          order,
          orderItems: mockOrderItems,
          paymentMethod,
          total,
          promoCode: appliedPromo?.code
        }
      });
    } catch (error: any) {
      console.error('Payment failed:', error);
      showError(error.message || 'Failed to process payment. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApplePayment = () => handleSubmit();
  const handleGooglePayment = () => handleSubmit();
  const handleCrossmintPayment = () => handleSubmit();

  return (
    <div className="min-h-screen theme-transition relative overflow-hidden" style={{background: 'var(--liquid-gradient-bg)'}}>
      {/* Enhanced Liquid Glass Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-96 h-96 liquid-glass opacity-20 liquid-animate-morph"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 liquid-glass-strong opacity-15 liquid-animate-float" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/3 w-64 h-64 liquid-glass opacity-10 liquid-animate-wave" style={{animationDelay: '4s'}}></div>
        <div className="absolute top-20 right-1/4 w-48 h-48 liquid-glass opacity-8 liquid-animate-pulse" style={{animationDelay: '6s'}}></div>
        <div className="absolute bottom-40 left-1/4 w-72 h-72 liquid-glass opacity-12 liquid-animate-morph" style={{animationDelay: '8s'}}></div>
      </div>
      
      <div className="relative z-10 pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <Button
                variant="glass"
                size="icon"
                onClick={() => navigate(-1)}
                className="w-12 h-12 rounded-full"
              >
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-3xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>Checkout</h1>
                <p style={{color: 'var(--liquid-text-muted)'}}>Complete your order securely</p>
              </div>
            </div>
            
            {/* Security Badge */}
            <div className="flex items-center space-x-2 glass px-4 py-2 rounded-2xl">
              <Shield size={20} className="text-green-400" />
              <span className="text-sm font-medium" style={{color: 'var(--liquid-text-primary)'}}>SSL Secured</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Forms */}
            <div className="lg:col-span-2 space-y-8">
              {/* Contact Information */}
              <Card variant="glass" className="p-8">
                <h2 className="text-2xl font-bold mb-6 flex items-center" style={{color: 'var(--liquid-text-primary)'}}>
                  <User size={24} className="mr-3 text-blue-400" />
                  Contact Information
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>First Name</label>
                    <div className="relative">
                      <User size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2" style={{color: 'var(--liquid-text-muted)'}} />
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className="w-full pl-12 pr-4 py-4 glass-input rounded-2xl focus-glass"
                        style={{color: 'var(--liquid-text-primary)'}}
                        placeholder="Enter first name"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Last Name</label>
                    <div className="relative">
                      <User size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2" style={{color: 'var(--liquid-text-muted)'}} />
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className="w-full pl-12 pr-4 py-4 glass-input rounded-2xl focus-glass"
                        style={{color: 'var(--liquid-text-primary)'}}
                        placeholder="Enter last name"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Email</label>
                    <div className="relative">
                      <Mail size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2" style={{color: 'var(--liquid-text-muted)'}} />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="w-full pl-12 pr-4 py-4 glass-input rounded-2xl focus-glass"
                        style={{color: 'var(--liquid-text-primary)'}}
                        placeholder="Enter email address"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Phone</label>
                    <div className="relative">
                      <Phone size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 opacity-60" style={{color: 'var(--liquid-text-primary)'}} />
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="w-full pl-12 pr-4 py-4 glass-input rounded-2xl  placeholder-white/50 focus-glass" style={{color: 'var(--liquid-text-primary)'}}
                        placeholder="+962 XX XXX XXXX"
                        required
                      />
                    </div>
                  </div>
                </div>
              </Card>

              {/* Multi-Booking: Quantity and Attendee Names */}
              {bookingType === 'multi' && (
                <Card variant="glass" className="p-8">
                  <h2 className="text-2xl font-bold mb-6 flex items-center" style={{color: 'var(--liquid-text-primary)'}}>
                    <User size={24} className="mr-3 text-green-400" />
                    Attendee Information
                  </h2>

                  {/* Quantity Selector */}
                  <div className="glass rounded-2xl p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-lg font-semibold" style={{color: 'var(--liquid-text-primary)'}}>
                        Quantity
                      </label>
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => {
                            const newQty = Math.max(1, checkoutQuantity - 1);
                            setCheckoutQuantity(newQty);
                            setAttendeeNames(Array(newQty).fill('').map((_, i) => attendeeNames[i] || ''));
                            // Update mockOrderItems quantity
                            if (mockOrderItems.length > 0) {
                              setMockOrderItems([{
                                ...mockOrderItems[0],
                                quantity: newQty
                              }]);
                            }
                          }}
                          className="w-10 h-10 rounded-full glass-button flex items-center justify-center font-bold hover:scale-110 transition-transform"
                          style={{color: 'var(--liquid-text-primary)'}}
                        >
                          -
                        </button>
                        <span className="text-xl font-bold w-12 text-center" style={{color: 'var(--liquid-text-primary)'}}>
                          {checkoutQuantity}
                        </span>
                        <button
                          onClick={() => {
                            const newQty = checkoutQuantity + 1;
                            setCheckoutQuantity(newQty);
                            setAttendeeNames([...attendeeNames, ...Array(newQty - attendeeNames.length).fill('')]);
                            // Update mockOrderItems quantity
                            if (mockOrderItems.length > 0) {
                              setMockOrderItems([{
                                ...mockOrderItems[0],
                                quantity: newQty
                              }]);
                            }
                          }}
                          className="w-10 h-10 rounded-full glass-button flex items-center justify-center font-bold hover:scale-110 transition-transform"
                          style={{color: 'var(--liquid-text-primary)'}}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Attendee Name Fields */}
                    <div className="space-y-4">
                      <label className="text-sm font-medium block" style={{color: 'var(--liquid-text-primary)'}}>
                        Attendee Full Names
                      </label>
                      {Array.from({ length: checkoutQuantity }).map((_, index) => (
                        <div key={index}>
                          <label className="block text-sm font-medium mb-2 opacity-70" style={{color: 'var(--liquid-text-primary)'}}>
                            Attendee {index + 1}
                          </label>
                          <input
                            type="text"
                            value={attendeeNames[index] || ''}
                            onChange={(e) => {
                              const newNames = [...attendeeNames];
                              newNames[index] = e.target.value;
                              setAttendeeNames(newNames);
                              // Save customer info when attendee name is filled
                              if (e.target.value.trim()) {
                                setTimeout(() => {
                                  saveCustomerInfo('info_filled');
                                }, 1000);
                              }
                            }}
                            className="w-full px-4 py-3 glass-input rounded-2xl focus-glass"
                            style={{color: 'var(--liquid-text-primary)'}}
                            placeholder={`Enter full name for attendee ${index + 1}`}
                            required
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              )}

              {/* Payment Method Selection */}
              <Card variant="glass" className="p-8">
                <h2 className="text-2xl font-bold  mb-6 flex items-center" style={{color: 'var(--liquid-text-primary)'}}>
                  <CreditCard size={24} className="mr-3 style={{color: '#f9bb10'}}" />
                  Payment Method
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                  {paymentMethods.map((method) => {
                    const IconComponent = method.icon;
                    return (
                      <button
                        key={method.id}
                        onClick={() => setPaymentMethod(method.id as 'card' | 'applePay' | 'googlePay' | 'crypto' | 'cash')}
                        className={`relative p-6 rounded-2xl border-2 transition-all duration-300 text-left hover:scale-105 ${
                          paymentMethod === method.id
                            ? 'border-yellow-400 ring-2 ring-yellow-400/30'
                            : 'border-white/20 hover:border-white/40 glass'
                        }`}
                        style={paymentMethod === method.id ? {backgroundColor: '#f9bb1020'} : {}}
                      >
                        <IconComponent size={32} className="mb-3" style={{color: 'var(--liquid-text-primary)'}} />
                        <h3 className="font-semibold  mb-1" style={{color: 'var(--liquid-text-primary)'}}>{method.label}</h3>
                        <p className="opacity-70 text-sm mb-2" style={{color: 'var(--liquid-text-primary)'}}>{method.description}</p>
                        <div className="flex items-center space-x-1">
                          <Clock size={14} className="text-green-400" />
                          <span className="text-green-400 text-xs">{method.processingTime}</span>
                        </div>
                        
                        {paymentMethod === method.id && (
                          <div className="absolute top-4 right-4">
                            <CheckCircle size={20} className="style={{color: '#f9bb10'}}" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Payment Details Forms */}
                {paymentMethod === 'card' && (
                  <div className="space-y-6 glass p-6 rounded-2xl">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold" style={{color: 'var(--liquid-text-primary)'}}>Card Details</h3>
                      <div className="flex items-center space-x-2">
                        <Lock size={16} className="text-green-400" />
                        <span className="text-green-400 text-sm">Secure</span>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Card Number</label>
                        <input
                          type="text"
                          value={formData.cardNumber}
                          onChange={(e) => handleInputChange('cardNumber', e.target.value)}
                          className="w-full px-4 py-4 glass-input rounded-2xl  placeholder-white/50 focus-glass" style={{color: 'var(--liquid-text-primary)'}}
                          placeholder="1234 5678 9012 3456"
                          maxLength={19}
                          required
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Expiry Date</label>
                          <input
                            type="text"
                            value={formData.expiryDate}
                            onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                            className="w-full px-4 py-4 glass-input rounded-2xl  placeholder-white/50 focus-glass" style={{color: 'var(--liquid-text-primary)'}}
                            placeholder="MM/YY"
                            maxLength={5}
                            required
                          />
                        </div>
                        <div>
                          <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>CVV</label>
                          <div className="relative">
                            <input
                              type={showCardDetails ? 'text' : 'password'}
                              value={formData.cvv}
                              onChange={(e) => handleInputChange('cvv', e.target.value)}
                              className="w-full px-4 py-4 pr-12 glass-input rounded-2xl  placeholder-white/50 focus-glass" style={{color: 'var(--liquid-text-primary)'}}
                              placeholder="123"
                              maxLength={4}
                              required
                            />
                            <button
                              type="button"
                              onClick={() => setShowCardDetails(!showCardDetails)}
                              className="absolute right-4 top-1/2 transform -translate-y-1/2 opacity-60 hover:text-white" style={{color: 'var(--liquid-text-primary)'}}
                            >
                              {showCardDetails ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Cardholder Name</label>
                        <input
                          type="text"
                          value={formData.cardholderName}
                          onChange={(e) => handleInputChange('cardholderName', e.target.value)}
                          className="w-full px-4 py-4 glass-input rounded-2xl  placeholder-white/50 focus-glass" style={{color: 'var(--liquid-text-primary)'}}
                          placeholder="Name as on card"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {paymentMethod === 'cash' && (
                  <div className="space-y-6 glass p-6 rounded-2xl">
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center">
                        <Truck size={24} className="text-orange-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold" style={{color: 'var(--liquid-text-primary)'}}>Cash on Delivery</h3>
                        <p className="opacity-70" style={{color: 'var(--liquid-text-primary)'}}>Pay when you receive your tickets at your doorstep</p>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Delivery Address</label>
                        <div className="relative">
                          <MapPin size={20} className="absolute left-4 top-4 opacity-60" style={{color: 'var(--liquid-text-primary)'}} />
                          <textarea
                            value={formData.deliveryAddress}
                            onChange={(e) => handleInputChange('deliveryAddress', e.target.value)}
                            className="w-full pl-12 pr-4 py-4 glass-input rounded-2xl  placeholder-white/50 focus-glass resize-none h-24" style={{color: 'var(--liquid-text-primary)'}}
                            placeholder="Enter your full delivery address"
                            required
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>City</label>
                        <select
                          value={formData.deliveryCity}
                          onChange={(e) => handleInputChange('deliveryCity', e.target.value)}
                          className="w-full px-4 py-4 glass-input rounded-2xl  bg-white/10 focus-glass" style={{color: 'var(--liquid-text-primary)'}}
                          required
                        >
                          <option value="" className="bg-gray-900" style={{color: 'var(--liquid-text-primary)'}}>Select City</option>
                          <option value="amman" className="bg-gray-900" style={{color: 'var(--liquid-text-primary)'}}>Amman</option>
                          <option value="irbid" className="bg-gray-900" style={{color: 'var(--liquid-text-primary)'}}>Irbid</option>
                          <option value="zarqa" className="bg-gray-900" style={{color: 'var(--liquid-text-primary)'}}>Zarqa</option>
                          <option value="aqaba" className="bg-gray-900" style={{color: 'var(--liquid-text-primary)'}}>Aqaba</option>
                          <option value="karak" className="bg-gray-900" style={{color: 'var(--liquid-text-primary)'}}>Karak</option>
                          <option value="madaba" className="bg-gray-900" style={{color: 'var(--liquid-text-primary)'}}>Madaba</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block  font-medium mb-2" style={{color: 'var(--liquid-text-primary)'}}>Delivery Notes (Optional)</label>
                        <textarea
                          value={formData.deliveryNotes}
                          onChange={(e) => handleInputChange('deliveryNotes', e.target.value)}
                          className="w-full px-4 py-4 glass-input rounded-2xl  placeholder-white/50 focus-glass resize-none h-20" style={{color: 'var(--liquid-text-primary)'}}
                          placeholder="Any special instructions for delivery..."
                        />
                      </div>
                    </div>
                    
                    <div className="bg-orange-500/20 border border-orange-400/30 rounded-2xl p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <Info size={16} className="text-orange-400" />
                        <span className="text-orange-400 font-medium">Delivery Information</span>
                      </div>
                      <ul className="opacity-80 text-sm space-y-1" style={{color: 'var(--liquid-text-primary)'}}>
                        <li>• Delivery within 24-48 hours</li>
                        <li>• Delivery fee: $5.00</li>
                        <li>• Available in major cities only</li>
                        <li>• Payment accepted in cash (exact amount preferred)</li>
                      </ul>
                    </div>
                  </div>
                )}

                {paymentMethod === 'applePay' && (
                  <div className="text-center py-8 glass rounded-2xl">
                    <div className="w-20 h-20 bg-black rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl" style={{color: 'var(--liquid-text-primary)'}}>🍎</span>
                    </div>
                    <h3 className="text-lg font-semibold  mb-2" style={{color: 'var(--liquid-text-primary)'}}>Apple Pay</h3>
                    <p className="opacity-70 mb-6" style={{color: 'var(--liquid-text-primary)'}}>Use Touch ID or Face ID to pay securely</p>
                    <Button
                      onClick={handleApplePayment}
                      variant="glass"
                      size="lg"
                    >
                      Pay with Apple Pay
                    </Button>
                  </div>
                )}

                {paymentMethod === 'googlePay' && (
                  <div className="text-center py-8 glass rounded-2xl">
                    <div className="w-20 h-20 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>G</span>
                    </div>
                    <h3 className="text-lg font-semibold  mb-2" style={{color: 'var(--liquid-text-primary)'}}>Google Pay</h3>
                    <p className="opacity-70 mb-6" style={{color: 'var(--liquid-text-primary)'}}>Quick and secure payment</p>
                    <Button
                      onClick={handleGooglePayment}
                      variant="glass"
                      size="lg"
                    >
                      Pay with Google Pay
                    </Button>
                  </div>
                )}

                {paymentMethod === 'crypto' && (
                  <div className="text-center py-8 glass rounded-2xl">
                    <div className="w-20 h-20 bg-gradient-to-r from-yellow-600 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl" style={{color: 'var(--liquid-text-primary)'}}>₿</span>
                    </div>
                    <h3 className="text-lg font-semibold  mb-2" style={{color: 'var(--liquid-text-primary)'}}>Crypto Payment</h3>
                    <p className="opacity-70 mb-6" style={{color: 'var(--liquid-text-primary)'}}>Pay with crypto and get NFT receipt</p>
                    <Button
                      onClick={handleCrossmintPayment}
                      variant="glass"
                      size="lg"
                    >
                      Pay with Crossmint
                    </Button>
                    <div className="mt-4 text-sm opacity-70" style={{color: 'var(--liquid-text-primary)'}}>
                      <p>✓ ETH, SOL, MATIC supported</p>
                      <p>✓ NFT ticket receipt included</p>
                    </div>
                  </div>
                )}
              </Card>
            </div>

            {/* Right Column - Order Summary */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Order Summary */}
                <Card variant="glass" className="p-6">
                  <h2 className="text-xl font-bold  mb-6" style={{color: 'var(--liquid-text-primary)'}}>Order Summary</h2>

                  {/* Event Items */}
                  <div className="space-y-4 mb-6">
                    {mockOrderItems.map((item, index) => (
                      <div key={index} className="flex space-x-4 p-4 glass rounded-2xl">
                        {item.image && (
                          <img 
                            src={item.image} 
                            alt={item.eventTitle}
                            className="w-16 h-16 rounded-xl object-cover"
                          />
                        )}
                        <div className="flex-1">
                          <h3 className="font-semibold  text-sm line-clamp-2" style={{color: 'var(--liquid-text-primary)'}}>
                            {item.eventTitle}
                          </h3>
                          <p className="/70 text-xs" style={{color: 'var(--liquid-text-primary)'}}>
                            {item.ticketType} × {item.quantity}
                          </p>
                          <p className="font-semibold" style={{color: 'var(--liquid-text-primary)'}}>
                            ${(item.price * item.quantity).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Promo Code Section */}
                  <div className="mb-6">
                    <div className="flex items-center space-x-2 mb-3">
                      <Gift size={18} className="text-green-400" />
                      <span className="font-medium" style={{color: 'var(--liquid-text-primary)'}}>Promo Code</span>
                    </div>
                    
                    {!appliedPromo ? (
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                          placeholder="Enter promo code"
                          className="flex-1 px-4 py-3 glass-input rounded-xl  placeholder-white/50 focus-glass text-sm" style={{color: 'var(--liquid-text-primary)'}}
                          maxLength={20}
                        />
                        <Button
                          onClick={applyPromoCode}
                          disabled={!promoCode.trim() || isApplyingPromo}
                          variant="glass"
                          size="sm"
                          loading={isApplyingPromo}
                        >
                          Apply
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-green-500/20 border border-green-400/30 rounded-xl">
                        <div className="flex items-center space-x-2">
                          <Tag size={16} className="text-green-400" />
                          <div>
                            <span className="text-green-400 font-medium">{appliedPromo.code}</span>
                            <p className="text-green-400/80 text-xs">{appliedPromo.description}</p>
                          </div>
                        </div>
                        <button
                          onClick={removePromoCode}
                          className="text-green-400 hover:text-green-300"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )}
                    
                    {promoError && (
                      <div className="flex items-center space-x-2 mt-2 text-red-400 text-sm">
                        <AlertCircle size={14} />
                        <span>{promoError}</span>
                      </div>
                    )}
                  </div>

                  {/* Price Breakdown */}
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between opacity-80" style={{color: 'var(--liquid-text-primary)'}}>
                      <span>Subtotal</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    
                    {appliedPromo && (
                      <div className="flex justify-between text-green-400">
                        <span>Discount ({appliedPromo.code})</span>
                        <span>-${promoDiscount.toFixed(2)}</span>
                      </div>
                    )}
                    
                    {processingFee > 0 && (
                      <div className="flex justify-between opacity-80" style={{color: 'var(--liquid-text-primary)'}}>
                        <span>Processing Fee</span>
                        <span>${processingFee.toFixed(2)}</span>
                      </div>
                    )}
                    
                    {deliveryFee > 0 && (
                      <div className="flex justify-between opacity-80" style={{color: 'var(--liquid-text-primary)'}}>
                        <span>Delivery Fee</span>
                        <span>${deliveryFee.toFixed(2)}</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between opacity-80" style={{color: 'var(--liquid-text-primary)'}}>
                      <span>Tax (16% VAT)</span>
                      <span>${taxes.toFixed(2)}</span>
                    </div>
                    
                    <div className="border-t border-white/20 pt-3">
                      <div className="flex justify-between text-xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>
                        <span>Total</span>
                        <span>${total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Place Order Button */}
                  <Button
                    onClick={handleSubmit}
                    disabled={isProcessing}
                    variant="glass"
                    size="lg"
                    className="w-full"
                    loading={isProcessing}
                  >
                    Place Order - ${total.toFixed(2)}
                  </Button>

                  {/* Security Note */}
                  <div className="flex items-center space-x-2 justify-center mt-4 opacity-60 text-sm" style={{color: 'var(--liquid-text-primary)'}}>
                    <Lock size={14} className="text-green-400" />
                    <span>Your payment information is secure and encrypted</span>
                  </div>
                </Card>

                {/* Available Promo Codes (for demo) */}
                {!appliedPromo && (
                  <Card variant="glass" className="p-6">
                    <h3 className="text-lg font-bold  mb-4 flex items-center" style={{color: 'var(--liquid-text-primary)'}}>
                      <Percent size={20} className="mr-2 text-yellow-400" />
                      Available Offers
                    </h3>
                    <div className="space-y-3">
                      {availablePromoCodes.filter(promo => subtotal >= (promo.minAmount || 0)).map((promo) => (
                        <button
                          key={promo.id}
                          onClick={() => {
                            setPromoCode(promo.code);
                            applyPromoCode();
                          }}
                          className="w-full p-3 glass rounded-xl text-left hover:scale-105 transition-transform"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-semibold" style={{color: 'var(--liquid-text-primary)'}}>{promo.code}</span>
                              <p className="opacity-70 text-xs" style={{color: 'var(--liquid-text-primary)'}}>{promo.description}</p>
                            </div>
                            <Plus size={16} className="opacity-60" style={{color: 'var(--liquid-text-primary)'}} />
                          </div>
                        </button>
                      ))}
                    </div>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>



      {/* Footer */}
      <Footer />

    </div>
  );
};

export default Checkout;
